import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";

/**
 * DeliveryHeatmap — where do orders come from?
 *
 * Renders a real ``leaflet.heat`` layer so density becomes a smooth
 * glowing gradient (hot spots = red, sparse edges = blue-green) rather
 * than the older stacked-CircleMarker approximation. Individual points
 * still get tooltips through an invisible marker layer so the manager
 * can hover a bright cluster and read out the exact drop-off.
 */
const StoreIcon = new L.DivIcon({
  className: "",
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#2563eb;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,.3)">S</div>`,
});

// Tiny transparent marker so tooltips still work over the heat blob.
const DotIcon = new L.DivIcon({
  className: "",
  iconSize: [10, 10],
  iconAnchor: [5, 5],
  html: `<div style="width:10px;height:10px;border-radius:50%;background:rgba(255,255,255,0.01);"></div>`,
});

const AutoFit = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
};

/**
 * HeatLayer — react-leaflet doesn't ship a heatmap primitive, so we
 * manually attach ``L.heatLayer`` to the underlying map instance and
 * tear it down on unmount / prop change. Weights the intensity by
 * order total (bigger tickets = hotter) but clamps so a single $200
 * catering run doesn't wash out the neighborhood pattern.
 */
const HeatLayer = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return undefined;
    const data = points.map((p) => {
      const weight = Math.min(1, Math.max(0.3, (p.total || 20) / 100));
      return [p.lat, p.lng, weight];
    });
    const layer = L.heatLayer(data, {
      radius: 30,
      blur: 22,
      maxZoom: 17,
      minOpacity: 0.35,
      gradient: {
        0.2: "#2563eb",
        0.4: "#22c55e",
        0.6: "#facc15",
        0.8: "#f97316",
        1.0: "#dc2626",
      },
    });
    layer.addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [points, map]);
  return null;
};

const DeliveryHeatmap = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/delivery/heatmap?days=${days}`);
      setData(res.data);
    } catch (e) {
      toast.error("Couldn't load heatmap");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const center = useMemo(() => {
    if (data?.store_lat != null) return [data.store_lat, data.store_lng];
    if (data?.points?.length) return [data.points[0].lat, data.points[0].lng];
    return [39, -95];
  }, [data]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Delivery Heatmap</CardTitle>
          <CardDescription>
            Every completed delivery from the last{" "}
            <b>{data?.days ?? days}</b> day(s) — {data?.count ?? 0} points.
            Brighter blobs mean more orders — spot where to extend range or open a new zone.
          </CardDescription>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32" data-testid="heatmap-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg overflow-hidden border" style={{ height: "60vh", minHeight: 400 }} data-testid="heatmap-map">
          <MapContainer center={center} zoom={12} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {data?.store_lat != null && (
              <Marker position={[data.store_lat, data.store_lng]} icon={StoreIcon}>
                <Tooltip permanent direction="top">Store</Tooltip>
              </Marker>
            )}
            <HeatLayer points={data?.points || []} />
            {(data?.points || []).map((p, i) => (
              <Marker key={i} position={[p.lat, p.lng]} icon={DotIcon} interactive={true}>
                <Tooltip>
                  {p.zone ? <>Zone: <b>{p.zone}</b><br /></> : null}
                  {p.distance_mi != null && <>{p.distance_mi.toFixed(1)} mi<br /></>}
                  {p.total != null && <>${p.total.toFixed(2)}</>}
                </Tooltip>
              </Marker>
            ))}
            {data?.points?.length > 0 && <AutoFit points={data.points} />}
          </MapContainer>
        </div>
        {/* Legend */}
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="heatmap-legend">
          <span>Low</span>
          <div
            className="h-2 flex-1 rounded-full"
            style={{
              background:
                "linear-gradient(to right,#2563eb 0%,#22c55e 25%,#facc15 50%,#f97316 75%,#dc2626 100%)",
            }}
          />
          <span>High</span>
        </div>
        {loading && <div className="text-xs text-muted-foreground mt-2">Loading…</div>}
        {!loading && data?.count === 0 && (
          <div className="text-sm text-muted-foreground mt-3">
            No delivered orders in this period. Once real deliveries flow, the heatmap will glow here.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeliveryHeatmap;
